import React, { useState } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useTranslation } from 'react-i18next'
import { FormField, FormGrid } from '../common/form'
import NumberInput from '../common/NumberInput'
import SelectPriority from '../common/SelectPriority'
import SelectStatut from '../common/SelectStatut'
import SelectTypeProjet from '../common/SelectTypeProjet'
import SelectobjMaturite from '../common/SelectObjM'
import SelectMission from '../common/SelectMission'
import SelectPlan from '../common/SelectPlan'
import SelectPriorityRisks from '../common/SelectPriorityRisks'
import SelectCriticality from '../common/IncidentCriticality'
import SelectIncidentPerimeter from '../common/IncidentPerimete'
import SelectIncidentTypes from '../common/IncidentTypes'
import SelectPipeline from '../../views/pipeline/SelectPipeline'
import { addMissionApi } from '../../api/missionsApi'
import { addPipelineApi } from '../../api/pipelineApi'
import { addProjetApi } from '../../api/projetApi'
import { addAuditApi } from '../../api/auditApi'
import { addIncidentApi } from '../../api/incidentApi'
import { addRisqueApi } from '../../api/risqueApi'
import { addPlanApi } from '../../api/planApi'
import { Mission } from '../../types/missionTypes'
import { Pipeline } from '../../types/pipelineTypes'
import { Projet } from '../../types/projetTypes'
import { Audit } from '../../types/auditTypes'
import { Incident } from '../../types/incidentTypes'
import { Risque } from '../../types/risqueTypes'
import { Plan } from '../../types/planTypes'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import { formatTableDate } from '../../utils/formatDate'
import QuickCreateDialog from './QuickCreateDialog'
import { MissionSummaryCard, PlanSummaryCard } from './ParentSummaryCard'
import { QuickEntity, friendlyCreateError } from './quickCreateErrors'
import { fromInputDate, toInputDate, useMissionBudget, usePlanInfo } from './parentInfo'

type Created = { _id?: string }

type QuickFormProps<T> = {
  initialName?: string
  missionId?: string
  onCreated: (created: T) => void
  onCancel: () => void
}

function useQuickSubmit<T extends Created>(entity: QuickEntity, onCreated: (created: T) => void) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const run = async (hasErrors: boolean, create: () => Promise<T>) => {
    setSubmitted(true)
    if (hasErrors || busy) return
    setBusy(true)
    setError(null)
    try {
      const created = await create()
      if (!created?._id) throw new Error('')
      onCreated(created)
    } catch (e) {
      setError(friendlyCreateError(e, entity, t))
      setBusy(false)
    }
  }

  return { busy, error, submitted, run }
}

type Errors = Record<string, string | undefined>
const hasAny = (errors: Errors) => Object.values(errors).some(Boolean)

function FieldMessage({ error, warning, show }: { error?: string; warning?: string; show: boolean }) {
  if (show && error)
    return (
      <span className="qc-field-error" role="alert">
        {error}
      </span>
    )
  if (warning) return <span className="qc-field-warn">{warning}</span>
  return null
}

function LockedMission({ name }: { name?: string }) {
  const { t } = useTranslation()
  return (
    <div className="qc-locked" title={t('quickCreate.lockedParent')}>
      {name || '…'}
    </div>
  )
}

const today = () => toInputDate(new Date())

function dateRangeErrors(t: (k: string) => string, start: string, end: string): Errors {
  const s = fromInputDate(start)
  const e = fromInputDate(end)
  return {
    DateDebut: !start ? t('quickCreate.validation.required') : !s ? t('quickCreate.validation.date') : undefined,
    DateFin: !end
      ? t('quickCreate.validation.required')
      : !e
        ? t('quickCreate.validation.date')
        : s && e < s
          ? t('quickCreate.validation.endBeforeStart')
          : undefined,
  }
}

/* ----------------------------- Mission ----------------------------- */

export function QuickMissionForm({ initialName, onCreated, onCancel }: QuickFormProps<Mission>) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    MissionName: initialName ?? '',
    Description: '',
    Budget: null as number | null,
  })
  const { busy, error, submitted, run } = useQuickSubmit<Mission>('mission', onCreated)
  const required = t('quickCreate.validation.required')
  const errors: Errors = {
    MissionName: form.MissionName.trim() ? undefined : required,
    Description: form.Description.trim() ? undefined : required,
    Budget:
      form.Budget === null ? required : form.Budget <= 0 ? t('quickCreate.validation.positive') : undefined,
  }

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.mission')}
      subtitle={t('quickCreate.subtitle.mission')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addMissionApi({
            MissionName: form.MissionName.trim(),
            Description: form.Description.trim(),
            Budget: form.Budget ?? 0,
          } as Mission)
        )
      }
    >
      <FormGrid cols={2}>
        <FormField label={t('Name')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={form.MissionName}
            onChange={(e) => setForm((f) => ({ ...f, MissionName: e.target.value }))}
            aria-invalid={submitted && Boolean(errors.MissionName)}
          />
          <FieldMessage show={submitted} error={errors.MissionName} />
        </FormField>
        <FormField label={t('Budget')} required>
          <NumberInput
            currency
            min={0}
            value={form.Budget}
            onChange={(v) => setForm((f) => ({ ...f, Budget: v }))}
            aria-invalid={submitted && Boolean(errors.Budget)}
          />
          <FieldMessage show={submitted} error={errors.Budget} />
        </FormField>
        <FormField label={t('Description')} required span>
          <textarea
            className="incyia-form-inp"
            rows={3}
            value={form.Description}
            onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))}
            aria-invalid={submitted && Boolean(errors.Description)}
          />
          <FieldMessage show={submitted} error={errors.Description} />
        </FormField>
      </FormGrid>
    </QuickCreateDialog>
  )
}

/* ----------------------------- Pipeline ---------------------------- */

export function QuickPipelineForm({ initialName, missionId, onCreated, onCancel }: QuickFormProps<Pipeline>) {
  const { t } = useTranslation()
  const mission = useMissionBudget(missionId)
  const [name, setName] = useState(initialName ?? '')
  const { busy, error, submitted, run } = useQuickSubmit<Pipeline>('pipeline', onCreated)
  const errors: Errors = { PipelineName: name.trim() ? undefined : t('quickCreate.validation.required') }

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.pipeline')}
      subtitle={t('quickCreate.subtitle.pipeline')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addPipelineApi({ PipelineName: name.trim(), MissionId: missionId } as Pipeline)
        )
      }
    >
      <MissionSummaryCard info={mission.data} status={mission.status} />
      <FormGrid cols={2}>
        <FormField label={t('Name')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={submitted && Boolean(errors.PipelineName)}
          />
          <FieldMessage show={submitted} error={errors.PipelineName} />
        </FormField>
        <FormField label={t('Mission')}>
          <LockedMission name={mission.data?.mission.MissionName} />
        </FormField>
      </FormGrid>
    </QuickCreateDialog>
  )
}

/* ----------------------------- Project ----------------------------- */

export function QuickProjectForm({ initialName, missionId, onCreated, onCancel }: QuickFormProps<Projet>) {
  const { t, i18n } = useTranslation()
  const fmt = useNumberFormat()
  const mission = useMissionBudget(missionId)
  const [form, setForm] = useState({
    ProjetName: initialName ?? '',
    Description: '',
    TypeProjet: 'Autres',
    Status: 'A faire',
    Priority: 3,
    DateDebut: today(),
    DateFin: today(),
    Budget: null as number | null,
    PipelineId: '',
  })
  const [pipelineDraft, setPipelineDraft] = useState<string | null>(null)
  const [pipelineKey, setPipelineKey] = useState(0)
  const { busy, error, submitted, run } = useQuickSubmit<Projet>('project', onCreated)
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  const required = t('quickCreate.validation.required')
  const remaining = mission.data?.remaining
  const errors: Errors = {
    ProjetName: form.ProjetName.trim() ? undefined : required,
    Description: form.Description.trim() ? undefined : required,
    Budget:
      form.Budget === null
        ? required
        : form.Budget < 0
          ? t('quickCreate.validation.positive')
          : remaining !== undefined && form.Budget > remaining
            ? t('quickCreate.validation.budgetOverMission', { remaining: fmt.money(Math.max(0, remaining)) })
            : undefined,
    ...dateRangeErrors(t, form.DateDebut, form.DateFin),
  }
  const start = fromInputDate(form.DateDebut)
  const missionCreated = mission.data?.createdAt ? new Date(mission.data.createdAt) : null
  const startWarning =
    start && missionCreated && start < new Date(missionCreated.toDateString())
      ? t('quickCreate.warnings.beforeMissionCreated', {
          date: formatTableDate(missionCreated, i18n.language),
        })
      : undefined

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.project')}
      subtitle={t('quickCreate.subtitle.project')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addProjetApi({
            ProjetName: form.ProjetName.trim(),
            Description: form.Description.trim(),
            // Hidden on this form. The API still requires Source.
            Source: 'N/A',
            TypeProjet: form.TypeProjet,
            Status: form.Status,
            Priority: Number(form.Priority),
            DateDebut: fromInputDate(form.DateDebut) as Date,
            DateFin: fromInputDate(form.DateFin) as Date,
            Budget: form.Budget ?? 0,
            MissionId: missionId,
            PipelineId: form.PipelineId,
          } as unknown as Projet)
        )
      }
    >
      <MissionSummaryCard info={mission.data} status={mission.status} />
      <FormGrid cols={2}>
        <FormField label={t('Project Name')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={form.ProjetName}
            onChange={(e) => set({ ProjetName: e.target.value })}
            aria-invalid={submitted && Boolean(errors.ProjetName)}
          />
          <FieldMessage show={submitted} error={errors.ProjetName} />
        </FormField>
        <FormField label={t('Mission')}>
          <LockedMission name={mission.data?.mission.MissionName} />
        </FormField>
        <FormField label={t('Pipeline')} span>
          <SelectPipeline
            value={form.PipelineId}
            onChange={(e: SelectChangeEvent<string>) => set({ PipelineId: e.target.value })}
            MissionId={missionId || ''}
            disabled={false}
            reloadKey={pipelineKey}
            onCreateNew={(search) => setPipelineDraft(search)}
          />
        </FormField>
        <FormField label={t('Budget')} required span>
          <NumberInput
            currency
            min={0}
            value={form.Budget}
            onChange={(v) => set({ Budget: v })}
            aria-invalid={submitted && Boolean(errors.Budget)}
          />
          <FieldMessage show={submitted} error={errors.Budget} />
        </FormField>
        <FormField label={t('actionImport.fields.startDate')} required>
          <input
            className="incyia-form-inp"
            type="date"
            value={form.DateDebut}
            onChange={(e) => set({ DateDebut: e.target.value })}
            aria-invalid={submitted && Boolean(errors.DateDebut)}
          />
          <FieldMessage show={submitted} error={errors.DateDebut} warning={startWarning} />
        </FormField>
        <FormField label={t('actionImport.fields.endDate')} required>
          <input
            className="incyia-form-inp"
            type="date"
            min={form.DateDebut || undefined}
            value={form.DateFin}
            onChange={(e) => set({ DateFin: e.target.value })}
            aria-invalid={submitted && Boolean(errors.DateFin)}
          />
          <FieldMessage show={submitted} error={errors.DateFin} />
        </FormField>
        <FormField label={t('Project Type')}>
          <SelectTypeProjet value={form.TypeProjet} onChange={(e) => set({ TypeProjet: e.target.value })} />
        </FormField>
        <FormField label={t('Status')}>
          <SelectStatut value={form.Status} onChange={(e) => set({ Status: e.target.value })} />
        </FormField>
        <FormField label={t('Priority')}>
          <SelectPriority value={form.Priority} onChange={(e) => set({ Priority: Number(e.target.value) })} />
        </FormField>
        <FormField label={t('Description')} required span>
          <textarea
            className="incyia-form-inp"
            rows={3}
            value={form.Description}
            onChange={(e) => set({ Description: e.target.value })}
            aria-invalid={submitted && Boolean(errors.Description)}
          />
          <FieldMessage show={submitted} error={errors.Description} />
        </FormField>
      </FormGrid>

      {pipelineDraft !== null ? (
        <QuickPipelineForm
          initialName={pipelineDraft}
          missionId={missionId}
          onCancel={() => setPipelineDraft(null)}
          onCreated={(pipeline) => {
            setPipelineDraft(null)
            setPipelineKey((k) => k + 1)
            set({ PipelineId: pipeline._id as string })
          }}
        />
      ) : null}
    </QuickCreateDialog>
  )
}

/* ------------------------------ Audit ------------------------------ */

export function QuickAuditForm({ initialName, missionId, onCreated, onCancel }: QuickFormProps<Audit>) {
  const { t } = useTranslation()
  const mission = useMissionBudget(missionId)
  const [form, setForm] = useState({ AuditName: initialName ?? '', Description: '' })
  const { busy, error, submitted, run } = useQuickSubmit<Audit>('audit', onCreated)
  const required = t('quickCreate.validation.required')
  const errors: Errors = {
    AuditName: form.AuditName.trim() ? undefined : required,
    Description: form.Description.trim() ? undefined : required,
  }

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.audit')}
      subtitle={t('quickCreate.subtitle.source')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addAuditApi({
            AuditName: form.AuditName.trim(),
            Description: form.Description.trim(),
            MissionId: missionId,
          } as Audit)
        )
      }
    >
      <MissionSummaryCard info={mission.data} status={mission.status} />
      <FormGrid cols={2}>
        <FormField label={t('Name')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={form.AuditName}
            onChange={(e) => setForm((f) => ({ ...f, AuditName: e.target.value }))}
            aria-invalid={submitted && Boolean(errors.AuditName)}
          />
          <FieldMessage show={submitted} error={errors.AuditName} />
        </FormField>
        <FormField label={t('Mission')}>
          <LockedMission name={mission.data?.mission.MissionName} />
        </FormField>
        <FormField label={t('Description')} required span>
          <textarea
            className="incyia-form-inp"
            rows={3}
            value={form.Description}
            onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))}
            aria-invalid={submitted && Boolean(errors.Description)}
          />
          <FieldMessage show={submitted} error={errors.Description} />
        </FormField>
      </FormGrid>
    </QuickCreateDialog>
  )
}

/* ----------------------------- Incident ---------------------------- */

export function QuickIncidentForm({ initialName, missionId, onCreated, onCancel }: QuickFormProps<Incident>) {
  const { t } = useTranslation()
  const mission = useMissionBudget(missionId)
  const [form, setForm] = useState({
    IncidentName: initialName ?? '',
    Description: '',
    Perimeter: 'Réseau',
    Criticality: 1,
    Type: 'Intrusion',
    Vulnerability: '',
    SLA: 0 as number | null,
  })
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))
  const { busy, error, submitted, run } = useQuickSubmit<Incident>('incident', onCreated)
  const required = t('quickCreate.validation.required')
  const errors: Errors = {
    IncidentName: form.IncidentName.trim() ? undefined : required,
    Description: form.Description.trim() ? undefined : required,
  }

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.incident')}
      subtitle={t('quickCreate.subtitle.source')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addIncidentApi({
            IncidentName: form.IncidentName.trim(),
            Description: form.Description.trim(),
            MissionId: missionId,
            Perimeter: form.Perimeter,
            Criticality: form.Criticality,
            Type: form.Type,
            Vulnerability: form.Vulnerability.trim(),
            SLA: form.SLA ?? 0,
          } as Incident)
        )
      }
    >
      <MissionSummaryCard info={mission.data} status={mission.status} />
      <FormGrid cols={2}>
        <FormField label={t('incidentUpdatePage.incidentName')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={form.IncidentName}
            onChange={(e) => set({ IncidentName: e.target.value })}
            aria-invalid={submitted && Boolean(errors.IncidentName)}
          />
          <FieldMessage show={submitted} error={errors.IncidentName} />
        </FormField>
        <FormField label={t('Mission')}>
          <LockedMission name={mission.data?.mission.MissionName} />
        </FormField>
        <FormField label={t('incidentUpdatePage.perimeter')}>
          <SelectIncidentPerimeter value={form.Perimeter} onChange={(e) => set({ Perimeter: e.target.value })} />
        </FormField>
        <FormField label={t('Criticality')}>
          <SelectCriticality value={form.Criticality} onChange={(e) => set({ Criticality: Number(e.target.value) })} />
        </FormField>
        <FormField label={t('Type')}>
          <SelectIncidentTypes value={form.Type} onChange={(e) => set({ Type: e.target.value })} />
        </FormField>
        <FormField label={t('incidentUpdatePage.sla')}>
          <NumberInput decimals={0} min={0} value={form.SLA} onChange={(v) => set({ SLA: v })} />
        </FormField>
        <FormField label={t('incidentUpdatePage.vulnerability')} span>
          <input
            className="incyia-form-inp"
            value={form.Vulnerability}
            onChange={(e) => set({ Vulnerability: e.target.value })}
          />
        </FormField>
        <FormField label={t('Description')} required span>
          <textarea
            className="incyia-form-inp"
            rows={3}
            value={form.Description}
            onChange={(e) => set({ Description: e.target.value })}
            aria-invalid={submitted && Boolean(errors.Description)}
          />
          <FieldMessage show={submitted} error={errors.Description} />
        </FormField>
      </FormGrid>
    </QuickCreateDialog>
  )
}

/* ------------------------------- Plan ------------------------------ */

export function QuickPlanForm({ initialName, missionId, onCreated, onCancel }: QuickFormProps<Plan>) {
  const { t } = useTranslation()
  const lockedMission = Boolean(missionId)
  const [pickedMission, setPickedMission] = useState(missionId ?? '')
  const [missionDraft, setMissionDraft] = useState<string | null>(null)
  const [missionKey, setMissionKey] = useState(0)
  const effectiveMission = missionId || pickedMission
  const mission = useMissionBudget(effectiveMission || undefined)
  const [form, setForm] = useState({
    PlanName: initialName ?? '',
    Description: '',
    DateDebut: today(),
    DateFin: today(),
    Status: 'A faire',
    Priority: 3,
    ObjectifMaturite: '1',
  })
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))
  const { busy, error, submitted, run } = useQuickSubmit<Plan>('plan', onCreated)
  const required = t('quickCreate.validation.required')
  const errors: Errors = {
    PlanName: form.PlanName.trim() ? undefined : required,
    Description: form.Description.trim() ? undefined : required,
    MissionId: effectiveMission ? undefined : required,
    ...dateRangeErrors(t, form.DateDebut, form.DateFin),
  }

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.plan')}
      subtitle={t('quickCreate.subtitle.plan')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addPlanApi({
            PlanName: form.PlanName.trim(),
            Description: form.Description.trim(),
            MissionId: effectiveMission,
            DateDebut: fromInputDate(form.DateDebut) as Date,
            DateFin: fromInputDate(form.DateFin) as Date,
            Status: form.Status,
            Priority: form.Priority,
            ObjectifMaturite: form.ObjectifMaturite,
          } as unknown as Plan)
        )
      }
    >
      {effectiveMission ? <MissionSummaryCard info={mission.data} status={mission.status} /> : null}
      <FormGrid cols={2}>
        <FormField label={t('Name')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={form.PlanName}
            onChange={(e) => set({ PlanName: e.target.value })}
            aria-invalid={submitted && Boolean(errors.PlanName)}
          />
          <FieldMessage show={submitted} error={errors.PlanName} />
        </FormField>
        <FormField label={t('Mission')} required={!lockedMission}>
          {lockedMission ? (
            <LockedMission name={mission.data?.mission.MissionName} />
          ) : (
            <>
              <SelectMission
                value={pickedMission}
                onChange={setPickedMission}
                reloadKey={missionKey}
                onCreateNew={(search) => setMissionDraft(search)}
              />
              <FieldMessage show={submitted} error={errors.MissionId} />
            </>
          )}
        </FormField>
        <FormField label={t('actionImport.fields.startDate')} required>
          <input
            className="incyia-form-inp"
            type="date"
            value={form.DateDebut}
            onChange={(e) => set({ DateDebut: e.target.value })}
            aria-invalid={submitted && Boolean(errors.DateDebut)}
          />
          <FieldMessage show={submitted} error={errors.DateDebut} />
        </FormField>
        <FormField label={t('actionImport.fields.endDate')} required>
          <input
            className="incyia-form-inp"
            type="date"
            min={form.DateDebut || undefined}
            value={form.DateFin}
            onChange={(e) => set({ DateFin: e.target.value })}
            aria-invalid={submitted && Boolean(errors.DateFin)}
          />
          <FieldMessage show={submitted} error={errors.DateFin} />
        </FormField>
        <FormField label={t('Status')}>
          <SelectStatut value={form.Status} onChange={(e) => set({ Status: e.target.value })} />
        </FormField>
        <FormField label={t('Priority')}>
          <SelectPriority value={form.Priority} onChange={(e) => set({ Priority: Number(e.target.value) })} />
        </FormField>
        <FormField label={t('quickCreate.fields.maturityTarget')} span>
          <SelectobjMaturite
            value={form.ObjectifMaturite}
            onChange={(e) => set({ ObjectifMaturite: String(e.target.value) })}
          />
        </FormField>
        <FormField label={t('Description')} required span>
          <textarea
            className="incyia-form-inp"
            rows={3}
            value={form.Description}
            onChange={(e) => set({ Description: e.target.value })}
            aria-invalid={submitted && Boolean(errors.Description)}
          />
          <FieldMessage show={submitted} error={errors.Description} />
        </FormField>
      </FormGrid>

      {missionDraft !== null ? (
        <QuickMissionForm
          initialName={missionDraft}
          onCancel={() => setMissionDraft(null)}
          onCreated={(created) => {
            setMissionDraft(null)
            setMissionKey((k) => k + 1)
            setPickedMission(created._id as string)
          }}
        />
      ) : null}
    </QuickCreateDialog>
  )
}

/* ------------------------------- Risk ------------------------------ */

export function QuickRiskForm({ initialName, missionId, onCreated, onCancel }: QuickFormProps<Risque>) {
  const { t, i18n } = useTranslation()
  const [form, setForm] = useState({
    RisqueName: initialName ?? '',
    Description: '',
    PlanId: '',
    Priority: 3,
    Criticality: 1,
    Deadline: today(),
  })
  const [planDraft, setPlanDraft] = useState<string | null>(null)
  const [planKey, setPlanKey] = useState(0)
  const plan = usePlanInfo(form.PlanId || undefined)
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))
  const { busy, error, submitted, run } = useQuickSubmit<Risque>('risk', onCreated)
  const required = t('quickCreate.validation.required')
  const deadline = fromInputDate(form.Deadline)
  const errors: Errors = {
    RisqueName: form.RisqueName.trim() ? undefined : required,
    Description: form.Description.trim() ? undefined : required,
    PlanId: form.PlanId ? undefined : required,
    Deadline: !form.Deadline ? required : !deadline ? t('quickCreate.validation.date') : undefined,
  }
  const planStart = plan.data?.DateDebut ? new Date(plan.data.DateDebut) : null
  const planEnd = plan.data?.DateFin ? new Date(plan.data.DateFin) : null
  const deadlineWarning =
    deadline && planStart && planEnd && (deadline < new Date(planStart.toDateString()) || deadline > planEnd)
      ? t('quickCreate.warnings.outsidePlan', {
          start: formatTableDate(planStart, i18n.language),
          end: formatTableDate(planEnd, i18n.language),
        })
      : undefined

  return (
    <QuickCreateDialog
      title={t('quickCreate.new.risk')}
      subtitle={t('quickCreate.subtitle.risk')}
      busy={busy}
      error={error}
      submitLabel={t('quickCreate.create')}
      onClose={onCancel}
      onSubmit={() =>
        run(hasAny(errors), () =>
          addRisqueApi({
            RisqueName: form.RisqueName.trim(),
            Description: form.Description.trim(),
            PlanId: form.PlanId,
            Criticality: form.Criticality,
            Priority: form.Priority,
            Deadline: deadline as Date,
          } as Risque)
        )
      }
    >
      {form.PlanId ? <PlanSummaryCard plan={plan.data} status={plan.status} /> : null}
      <FormGrid cols={2}>
        <FormField label={t('Risk name')} required>
          <input
            className="incyia-form-inp"
            autoFocus
            value={form.RisqueName}
            onChange={(e) => set({ RisqueName: e.target.value })}
            aria-invalid={submitted && Boolean(errors.RisqueName)}
          />
          <FieldMessage show={submitted} error={errors.RisqueName} />
        </FormField>
        <FormField label={t('Treatment plan')} required>
          <SelectPlan
            value={form.PlanId}
            onChange={(e) => set({ PlanId: String(e.target.value) })}
            MissionId={missionId}
            reloadKey={planKey}
            onCreateNew={(search) => setPlanDraft(search)}
          />
          <FieldMessage show={submitted} error={errors.PlanId} />
        </FormField>
        <FormField label={t('Priority')}>
          <SelectPriorityRisks value={form.Priority} onChange={(e) => set({ Priority: Number(e.target.value) })} />
        </FormField>
        <FormField label={t('Criticality')}>
          <SelectCriticality value={form.Criticality} onChange={(e) => set({ Criticality: Number(e.target.value) })} />
        </FormField>
        <FormField label={t('Deadline')} required span>
          <input
            className="incyia-form-inp"
            type="date"
            value={form.Deadline}
            onChange={(e) => set({ Deadline: e.target.value })}
            aria-invalid={submitted && Boolean(errors.Deadline)}
          />
          <FieldMessage show={submitted} error={errors.Deadline} warning={deadlineWarning} />
        </FormField>
        <FormField label={t('Description')} required span>
          <textarea
            className="incyia-form-inp"
            rows={3}
            value={form.Description}
            onChange={(e) => set({ Description: e.target.value })}
            aria-invalid={submitted && Boolean(errors.Description)}
          />
          <FieldMessage show={submitted} error={errors.Description} />
        </FormField>
      </FormGrid>

      {planDraft !== null ? (
        <QuickPlanForm
          initialName={planDraft}
          missionId={missionId}
          onCancel={() => setPlanDraft(null)}
          onCreated={(created) => {
            setPlanDraft(null)
            setPlanKey((k) => k + 1)
            set({ PlanId: created._id as string })
          }}
        />
      ) : null}
    </QuickCreateDialog>
  )
}
